'use client';

import {
  Fragment,
  useMemo,
  useCallback,
  useState,
  useEffect,
  useRef,
} from 'react';
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from '@headlessui/react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useRouter } from 'next/navigation';
import {
  Settings,
  StopCircle,
  Eye,
  Trash,
  Play,
  UserPlus,
  LayoutList,
  Edit,
  Download,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import type { Tournament } from '@/lib/types/auction';
import { StatusChangeDialog } from './StatusChangeDialog';
import { PlayerReportDialog } from './PlayerReportDialog';
import {
  validateOpenRegistration,
  validateCloseRegistration,
  validateStartAuction,
  validateEndAuction,
  getStatusChangeContent,
} from './tournamentStatusValidations';
import { handleError } from '@/lib/utils/error';
import toast from 'react-hot-toast';
import { tournamentService } from '@/services/api/tournaments';

interface TournamentActionsProps {
  tournament: Tournament;
  onAction?: (actionType?: string) => void;
}

interface MenuItemProps {
  onClick: () => void;
  icon: React.ElementType;
  label: string;
  className?: string;
}

const MenuItem = ({
  onClick,
  icon: Icon,
  label,
  className = '',
}: MenuItemProps) => (
  <button
    onClick={onClick}
    className={cn(
      'flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100',
      className
    )}
  >
    <Icon className="mr-3 h-4 w-4" />
    {label}
  </button>
);

export function TournamentActions({
  tournament,
  onAction,
}: TournamentActionsProps) {
  const router = useRouter();
  const { user } = useAuth();
  const [pendingAction, setPendingAction] = useState<string | null>(null);
  const [isStatusDialogOpen, setIsStatusDialogOpen] = useState(false);
  const [isReportDialogOpen, setIsReportDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isClient, setIsClient] = useState(false);
  const isMounted = useRef(true);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Ensure component only renders on client to prevent SSR issues
  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const isAdmin = useMemo(
    () => tournament?.owner?.id === user?.id,
    [tournament, user]
  );

  const handleStatusChange = useCallback(
    async (
      action: 'openRegistration' | 'closeRegistration' | 'start' | 'end'
    ) => {
      try {
        setIsLoading(true);

        switch (action) {
          case 'openRegistration':
            await tournamentService.openRegistration(tournament.id);
            break;
          case 'closeRegistration':
            await tournamentService.closeRegistration(tournament.id);
            break;
          case 'start':
            await tournamentService.start(tournament.id);
            break;
          case 'end':
            await tournamentService.end(tournament.id);
            break;
        }

        setIsStatusDialogOpen(false);
        setPendingAction(null);

        // Call onAction if provided
        if (onAction) {
          onAction();
        }

        // Special handling for start auction - redirect to auction page
        if (action === 'start') {
          router.push(`/dashboard/auction`);
        } else {
          // Force a hard navigation to ensure fresh data for other actions
          router.push(`/tournament/${tournament.id}`);
        }
      } catch (error) {
        toast.error(handleError(error, 'Updating tournament status'));
      } finally {
        setIsLoading(false);
      }
    },
    [tournament.id, onAction, router]
  );

  const handleAction = useCallback(
    async (action: string) => {
      if (
        ['openRegistration', 'closeRegistration', 'start', 'end'].includes(
          action
        )
      ) {
        setPendingAction(action);
        setIsStatusDialogOpen(true);
        return;
      }

      try {
        switch (action) {
          case 'delete':
            await tournamentService.delete(tournament.id);
            // Call onAction with the action type
            if (onAction) {
              onAction('delete');
            }
            break;
        }

        // Only call router.refresh if no onAction callback
        if (!onAction) {
          router.refresh();
        }
      } catch (error) {
        console.error('Action failed:', error);
      }
    },
    [tournament, onAction, router]
  );

  const statusValidation = useMemo(() => {
    if (!pendingAction) return null;

    switch (pendingAction) {
      case 'openRegistration':
        return validateOpenRegistration(tournament);
      case 'closeRegistration':
        return validateCloseRegistration(tournament);
      case 'start':
        return validateStartAuction(tournament);
      case 'end':
        return validateEndAuction(tournament);
      default:
        return null;
    }
  }, [pendingAction, tournament]);

  const statusContent = useMemo(
    () => getStatusChangeContent(tournament.status),
    [tournament.status]
  );

  const navigateTo = useCallback(
    (path: string) => {
      router.push(path);
    },
    [router]
  );

  const actions = useMemo(() => {
    if (!isAdmin || tournament.status === 'COMPLETED') return [];

    const items = [];

    items.push({
      type: 'view',
      component: (
        <MenuItem
          onClick={() => navigateTo(`/tournament/${tournament.id}`)}
          icon={Eye}
          label="View Details"
        />
      ),
    });

    if (tournament.status === 'DRAFT') {
      items.push(
        {
          type: 'edit',
          component: (
            <MenuItem
              onClick={() =>
                navigateTo(`/dashboard/tournaments/${tournament.id}/edit`)
              }
              icon={Edit}
              label="Edit Tournament"
            />
          ),
        },
        {
          type: 'setup',
          component: (
            <MenuItem
              onClick={() =>
                navigateTo(`/dashboard/tournaments/${tournament.id}/setup`)
              }
              icon={Settings}
              label="Setup Tournament"
            />
          ),
        },
        {
          type: 'pools',
          component: (
            <MenuItem
              onClick={() =>
                navigateTo(`/dashboard/tournaments/${tournament.id}/pools`)
              }
              icon={LayoutList}
              label="Manage Pools"
            />
          ),
        },
        {
          type: 'openRegistration',
          component: (
            <MenuItem
              onClick={() => handleAction('openRegistration')}
              icon={UserPlus}
              label="Open Registration"
              className="text-green-700"
            />
          ),
        }
      );
    }

    if (tournament.status === 'REGISTRATION') {
      items.push(
        {
          type: 'edit',
          component: (
            <MenuItem
              onClick={() =>
                navigateTo(`/dashboard/tournaments/${tournament.id}/edit`)
              }
              icon={Edit}
              label="Edit Tournament"
            />
          ),
        },
        {
          type: 'setup',
          component: (
            <MenuItem
              onClick={() =>
                navigateTo(`/dashboard/tournaments/${tournament.id}/setup`)
              }
              icon={Settings}
              label="Setup Tournament"
            />
          ),
        },
        {
          type: 'pools',
          component: (
            <MenuItem
              onClick={() =>
                navigateTo(`/dashboard/tournaments/${tournament.id}/pools`)
              }
              icon={LayoutList}
              label="Manage Pools"
            />
          ),
        },
        {
          type: 'closeRegistration',
          component: (
            <MenuItem
              onClick={() => handleAction('closeRegistration')}
              icon={Play}
              label="Close Registration"
              className="text-yellow-700"
            />
          ),
        }
      );
    }

    if (tournament.status === 'UPCOMING') {
      items.push(
        {
          type: 'start',
          component: (
            <MenuItem
              onClick={() => handleAction('start')}
              icon={Play}
              label="Start Auction"
              className="text-blue-700"
            />
          ),
        },
        {
          type: 'downloadPlayers',
          component: (
            <MenuItem
              onClick={() => setIsReportDialogOpen(true)}
              icon={Download}
              label="Download Players PDF"
            />
          ),
        }
      );
    }

    if (tournament.status === 'ACTIVE') {
      items.push({
        type: 'end',
        component: (
          <MenuItem
            onClick={() => handleAction('end')}
            icon={StopCircle}
            label="End Auction"
            className="text-yellow-700"
          />
        ),
      });
    }

    if (['DRAFT', 'REGISTRATION'].includes(tournament.status)) {
      items.push({
        type: 'delete',
        component: (
          <MenuItem
            onClick={() => handleAction('delete')}
            icon={Trash}
            label="Delete Tournament"
            className="text-red-700"
          />
        ),
      });
    }

    return items;
  }, [tournament, navigateTo, handleAction, isAdmin]);

  // Don't render HeadlessUI components during SSR
  if (!isClient || !isAdmin || !actions.length) return null;

  const primaryActions = actions.filter(
    (a) => !['start', 'end', 'delete'].includes(a.type)
  );
  const statusActions = actions.filter((a) =>
    ['start', 'end'].includes(a.type)
  );
  const destructiveActions = actions.filter((a) => a.type === 'delete');

  return (
    <>
      <div className="relative" ref={popoverRef}>
        <Popover className="relative">
          {({ open, close }) => (
            <>
              <PopoverButton
                onClick={(e) => {
                  e.stopPropagation();
                }}
                className={cn(
                  'inline-flex items-center rounded-lg bg-gradient-to-r from-primary-500 to-accent-blue px-4 py-2 text-sm font-medium text-white shadow-sm transition-all hover:shadow-md',
                  'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2'
                )}
              >
                <Settings className="mr-2 h-4 w-4" />
                Actions
                <ChevronDown
                  className={cn(
                    'ml-2 h-5 w-5 text-white/70 transition-transform',
                    open && 'rotate-180'
                  )}
                />
              </PopoverButton>

              <Transition
                as={Fragment}
                show={open}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <PopoverPanel
                  className="absolute right-0 top-full z-50 mt-2 w-56 origin-top-right rounded-lg bg-white/90 shadow-xl ring-1 ring-black/5 backdrop-blur-md focus:outline-none"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <div className="py-1">
                    {primaryActions.map((a, i) => (
                      <div
                        key={i}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          close();
                          setTimeout(() => {
                            if (isMounted.current) {
                              a.component.props.onClick();
                            }
                          }, 100);
                        }}
                      >
                        {a.component}
                      </div>
                    ))}
                  </div>

                  {statusActions.length > 0 && (
                    <div className="border-t border-gray-200 py-1">
                      {statusActions.map((a, i) => (
                        <div
                          key={i}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            close();
                            setTimeout(() => {
                              if (isMounted.current) {
                                a.component.props.onClick();
                              }
                            }, 100);
                          }}
                        >
                          {a.component}
                        </div>
                      ))}
                    </div>
                  )}

                  {destructiveActions.length > 0 && (
                    <div className="border-t border-gray-200 py-1">
                      {destructiveActions.map((a, i) => (
                        <div
                          key={i}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            close();
                            setTimeout(() => {
                              if (isMounted.current) {
                                a.component.props.onClick();
                              }
                            }, 100);
                          }}
                        >
                          {a.component}
                        </div>
                      ))}
                    </div>
                  )}
                </PopoverPanel>
              </Transition>
            </>
          )}
        </Popover>
      </div>

      {statusValidation && (
        <StatusChangeDialog
          isOpen={isStatusDialogOpen}
          onClose={() => {
            setIsStatusDialogOpen(false);
            setPendingAction(null);
          }}
          onConfirm={() =>
            handleStatusChange(
              pendingAction as
                | 'openRegistration'
                | 'closeRegistration'
                | 'start'
                | 'end'
            )
          }
          title={statusContent.title}
          description={statusContent.description}
          impacts={statusContent.impacts}
          validations={statusValidation.validations}
          warningLevel={statusContent.warningLevel}
          isLoading={isLoading}
        />
      )}

      <PlayerReportDialog
        isOpen={isReportDialogOpen}
        onClose={() => setIsReportDialogOpen(false)}
        tournament={tournament}
        onAction={onAction}
      />
    </>
  );
}
